/**
 * Return Address Settings Component
 * Allows organizations to configure and verify their return address for outgoing mail.
 */

import React, { useState, useEffect } from 'react';
import { MapPin, CheckCircle, AlertCircle, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import lettersApi, { ReturnAddress } from '@/services/lettersApi';

interface ReturnAddressSettingsProps {
  organizationId: number;
}

export function ReturnAddressSettings({ organizationId }: ReturnAddressSettingsProps) {
  // Form state
  const [name, setName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Load existing return address on mount
  useEffect(() => {
    async function loadAddress() {
      setLoading(true);
      try {
        const address = await lettersApi.getReturnAddress();
        if (address) {
          setName(address.name || '');
          setAddressLine1(address.address_line1 || '');
          setAddressLine2(address.address_line2 || '');
          setCity(address.city || '');
          setState(address.state || '');
          setZipCode(address.zip_code || '');
          setIsVerified(address.is_verified);
        }
      } catch {
        setFeedback({ type: 'error', message: 'Failed to load return address.' });
      } finally {
        setLoading(false);
      }
    }
    loadAddress();
  }, [organizationId]);

  // Auto-dismiss feedback after 4 seconds
  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  // Mark address as unverified when any address field changes
  const handleAddressFieldChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string
  ) => {
    setter(value);
    if (isVerified) {
      setIsVerified(false);
      setVerificationMessage('');
    }
  };

  const handleVerifyAddress = async () => {
    if (!addressLine1 || !city || !state || !zipCode) {
      setFeedback({ type: 'error', message: 'Please fill in Address Line 1, City, State, and Zip Code before verifying.' });
      return;
    }

    setVerifying(true);
    setVerificationMessage('');
    try {
      const result = await lettersApi.verifyAddress({
        address_line1: addressLine1,
        address_line2: addressLine2 || undefined,
        city,
        state,
        zip_code: zipCode,
      });

      setIsVerified(result.verified);
      if (result.verified) {
        setVerificationMessage(result.standardized || 'Address verified successfully.');
      } else {
        const errorDetail = result.errors?.join(', ') || result.deliverability || 'Address could not be verified.';
        setVerificationMessage(errorDetail);
      }
    } catch {
      setIsVerified(false);
      setVerificationMessage('Verification service unavailable. Please try again later.');
    } finally {
      setVerifying(false);
    }
  };

  const handleSave = async () => {
    if (!name || !addressLine1 || !city || !state || !zipCode) {
      setFeedback({ type: 'error', message: 'Please fill in all required fields (Name, Address Line 1, City, State, Zip Code).' });
      return;
    }

    setSaving(true);
    try {
      await lettersApi.upsertReturnAddress({
        name,
        address_line1: addressLine1,
        address_line2: addressLine2 || undefined,
        city,
        state,
        zip_code: zipCode,
      });
      setFeedback({ type: 'success', message: 'Return address saved successfully.' });
    } catch {
      setFeedback({ type: 'error', message: 'Failed to save return address. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading return address...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-muted-foreground" />
          <CardTitle>Return Address</CardTitle>
        </div>
        <CardDescription>
          Configure the return address printed on outgoing mail. Verify the address to ensure deliverability.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Feedback toast */}
        {feedback && (
          <div
            className={`flex items-center gap-2 rounded-md border px-4 py-3 text-sm ${
              feedback.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200'
                : 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200'
            }`}
          >
            {feedback.type === 'success' ? (
              <CheckCircle className="h-4 w-4 shrink-0" />
            ) : (
              <AlertCircle className="h-4 w-4 shrink-0" />
            )}
            {feedback.message}
          </div>
        )}

        {/* Form fields */}
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label htmlFor="return-name">Name <span className="text-red-500">*</span></Label>
            <Input
              id="return-name"
              placeholder="Organization or individual name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="return-address1">Address Line 1 <span className="text-red-500">*</span></Label>
            <Input
              id="return-address1"
              placeholder="Street address"
              value={addressLine1}
              onChange={(e) => handleAddressFieldChange(setAddressLine1, e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="return-address2">Address Line 2</Label>
            <Input
              id="return-address2"
              placeholder="Apt, suite, unit, etc. (optional)"
              value={addressLine2}
              onChange={(e) => handleAddressFieldChange(setAddressLine2, e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="return-city">City <span className="text-red-500">*</span></Label>
              <Input
                id="return-city"
                placeholder="City"
                value={city}
                onChange={(e) => handleAddressFieldChange(setCity, e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="return-state">State <span className="text-red-500">*</span></Label>
              <Input
                id="return-state"
                placeholder="CA"
                maxLength={2}
                value={state}
                onChange={(e) => handleAddressFieldChange(setState, e.target.value.toUpperCase())}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="return-zip">Zip Code <span className="text-red-500">*</span></Label>
              <Input
                id="return-zip"
                placeholder="90210"
                maxLength={10}
                value={zipCode}
                onChange={(e) => handleAddressFieldChange(setZipCode, e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Verification status */}
        {isVerified !== null && (
          <div className="flex items-start gap-2">
            {isVerified ? (
              <Badge variant="default" className="bg-v2accent hover:bg-v2accent/90">
                <CheckCircle className="mr-1 h-3 w-3" />
                Verified
              </Badge>
            ) : (
              <Badge variant="destructive">
                <AlertCircle className="mr-1 h-3 w-3" />
                Unverified
              </Badge>
            )}
            {verificationMessage && (
              <span className="text-sm text-muted-foreground">{verificationMessage}</span>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={handleVerifyAddress}
            disabled={verifying || !addressLine1 || !city || !state || !zipCode}
          >
            {verifying ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <MapPin className="mr-2 h-4 w-4" />
            )}
            Verify Address
          </Button>

          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Return Address
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default ReturnAddressSettings;
